"use client";

import { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useAllLeagues from "@/hooks/useAllLeagues";
import AllLeaguesIcon from "@/assets/images/leagues/ic-all.svg";
import arrowDownIcon from "@/assets/images/kbv-icon-arrow-down-muted.svg";

export default function LeagueFilterV26({
  selectedIds = [],
  onChange,
  className = "",
  scopeClassName = "",
  themeStyle,
}) {
  const { leagues, loading } = useAllLeagues();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [tempSelectedIds, setTempSelectedIds] = useState(selectedIds || []);
  const [viewMode, setViewMode] = useState("league");
  const triggerRef = useRef(null);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    setTempSelectedIds(selectedIds || []);
  }, [selectedIds]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      const inTrigger = wrapperRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inTrigger && !inDropdown) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const updateDropdownPosition = () => {
    if (!triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    const padding = 8;
    const isMobile = window.innerWidth < 768;
    setDropdownStyle({
      position: "fixed",
      top: triggerRect.bottom + gap,
      ...(isMobile
        ? { left: padding, right: padding, width: window.innerWidth - padding * 2 }
        : { right: window.innerWidth - triggerRect.right, width: 550 }),
      zIndex: 9999,
    });
  };

  useLayoutEffect(() => {
    if (open && triggerRef.current) updateDropdownPosition();
  }, [open]);

  const normalizedLeagues = useMemo(
    () =>
      (leagues || []).map((l) => ({
        ...l,
        country: l.country || l.countryName || "Khác",
      })),
    [leagues],
  );

  const filteredLeagues = useMemo(() => {
    if (!keyword.trim()) return normalizedLeagues || [];
    const lower = keyword.toLowerCase();
    return (normalizedLeagues || []).filter((league) => {
      const nameVN = (league.nameVN || "").toLowerCase();
      const name = (league.name || "").toLowerCase();
      const country = (league.country || "").toLowerCase();
      return (
        nameVN.includes(lower) || name.includes(lower) || country.includes(lower)
      );
    });
  }, [normalizedLeagues, keyword]);

  const leaguesByCountry = useMemo(() => {
    const groups = {};
    for (const league of filteredLeagues) {
      const countryKey = league.country || "Khác";
      if (!groups[countryKey]) {
        groups[countryKey] = {
          country: countryKey,
          countryLogo: league.countryLogo || null,
          items: [],
        };
      }
      groups[countryKey].items.push(league);
    }
    return Object.values(groups);
  }, [filteredLeagues]);

  const handleToggleLeague = (id) => {
    setTempSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    const allIds = (filteredLeagues || []).map((l) => l.id);
    setTempSelectedIds(allIds);
  };

  const handleClearAll = () => {
    setTempSelectedIds([]);
  };

  const handleConfirm = () => {
    onChange?.(tempSelectedIds);
    setOpen(false);
  };

  const hasSelection = selectedIds && selectedIds.length > 0;
  const triggerLabel = hasSelection
    ? `${selectedIds.length} giải đấu`
    : "Tất cả giải đấu";

  return (
    <div ref={wrapperRef} className={`relative block overflow-visible h-[40px] w-full md:w-[276px] ${scopeClassName} ${className}`}>
      <button
        ref={triggerRef}
        className={`
          flex items-center justify-start rounded-[8px] cursor-pointer h-[40px] pl-[12px] relative transition-all duration-300 w-full outline-none box-border m-0 p-0 kbv-v26-filter-trigger
          ${open ? "kbv-v26-filter-open" : ""}
        `}
        type="button"
        onClick={() => {
          setOpen((prev) => {
            if (prev) return false;
            updateDropdownPosition();
            return true;
          });
        }}
      >
        <div className="flex items-center justify-start text-[var(--theme-text-muted)] text-[14px] leading-[20px] gap-[8px]">
          <div className="flex items-center justify-start w-[20px] h-[20px] rounded-[4px] overflow-hidden">
            <img
              src={AllLeaguesIcon}
              alt="all-leagues"
              className="w-[18px] h-[18px]"
              loading="lazy"
            />
          </div>
          <span className="truncate">
            {triggerLabel}
          </span>
        </div>

        <img
          src={arrowDownIcon}
          alt="arrow down"
          className={`absolute right-[12px] top-[8px] w-[24px] h-[24px] z-[1] transition-transform duration-300 ease-in-out ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ ...themeStyle, ...dropdownStyle }}
            className={`kbv-filter-v26-dropdown ${scopeClassName} flex max-h-[60vh] flex-col rounded-xl bg-[var(--theme-foreground)] text-[var(--theme-text)] shadow-xl box-border border border-solid border-[var(--theme-border)]`}
          >
            <div className="flex shrink-0 items-center justify-between px-3 pb-1.5 pt-2">
              <div className="relative min-w-0 flex-1">
                <input
                  type="text"
                  placeholder="Tìm tên giải đấu"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full rounded-lg border border-solid border-[var(--theme-border)] bg-[var(--theme-bg)] py-2 pl-2.5 pr-9 text-sm outline-none focus:border-[var(--theme-primary)] box-border m-0"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-muted)]">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="-m-2 ml-2 rounded p-2 hover:bg-[var(--theme-bg)] box-border border-none bg-transparent"
              >
                ✕
              </button>
            </div>

            <div className="shrink-0 border-b border-[var(--theme-border)] px-3 pb-2">
              <div className="flex items-center justify-between text-xs uppercase">
                <span className="font-medium text-[var(--theme-text-muted)]">Xem theo</span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("league")}
                    className={`rounded px-3 py-1.5 text-xs font-medium box-border m-0 border-solid ${viewMode === "league"
                      ? "border border-solid border-[var(--theme-primary)] bg-[var(--theme-foreground)] text-[var(--theme-primary)]"
                      : "border border-solid bg-[var(--theme-bg)] text-[var(--theme-text-muted)]"
                      }`}
                  >
                    Giải đấu
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("country")}
                    className={`rounded px-3 py-1.5 text-xs font-medium box-border m-0 border-solid ${viewMode === "country"
                      ? "border border-solid border-[var(--theme-primary)] bg-[var(--theme-foreground)] text-[var(--theme-primary)]"
                      : "border border-solid bg-[var(--theme-bg)] text-[var(--theme-text-muted)]"
                      }`}
                  >
                    Quốc gia
                  </button>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto py-2 pl-2">
              {loading ? (
                <div className="py-8 text-center text-sm text-[var(--theme-text-muted)]">
                  Đang tải danh sách giải đấu...
                </div>
              ) : filteredLeagues.length === 0 ? (
                <div className="py-8 text-center text-sm text-[var(--theme-text-muted)]">
                  Không tìm thấy giải đấu nào
                </div>
              ) : viewMode === "league" ? (
                <div className="grid grid-cols-2 gap-1.5">
                  {filteredLeagues.map((league) => {
                    const checked = tempSelectedIds.includes(league.id);
                    const displayName = league.nameVN || league.name || "";
                    return (
                      <button
                        key={league.id}
                        type="button"
                        onClick={() => handleToggleLeague(league.id)}
                        className="flex min-w-0 items-center gap-1.5 rounded border border-solid bg-[var(--theme-bg)] p-0 py-1.5 pl-0.5 pr-1.5 text-left text-xs transition-colors hover:bg-[var(--theme-accent)] box-border m-0 leading-tight"
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid text-[9px] font-bold ${checked
                            ? "border-[#2563eb] bg-[#2563eb] text-white"
                            : "border-[var(--theme-border)] bg-[var(--theme-bg)]"
                            }`}
                        >
                          {checked ? "✓" : ""}
                        </span>
                        {league.logo && (
                          <img
                            src={league.logo}
                            alt=""
                            className="h-5 w-5 shrink-0 object-contain"
                          />
                        )}
                        <span className="min-w-0 truncate text-left" title={league.nameVN || league.name}>
                          {displayName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {leaguesByCountry.map((group) => (
                    <div key={group.country}>
                      <div className="flex items-center gap-1.5 px-1 py-0.5 text-[11px] font-semibold uppercase text-[var(--theme-text-muted)]">
                        {group.countryLogo && (
                          <img
                            src={group.countryLogo}
                            alt={group.country}
                            className="h-5 w-5 object-contain"
                          />
                        )}
                        <span>{group.country}</span>
                      </div>
                      <div className="mt-1 grid grid-cols-2 gap-1.5">
                        {group.items.map((league) => {
                          const checked = tempSelectedIds.includes(league.id);
                          const displayName = league.nameVN || league.name || "";
                          return (
                            <button
                              key={league.id}
                              type="button"
                              onClick={() => handleToggleLeague(league.id)}
                              className="flex min-w-0 items-center gap-1.5 rounded border border-solid bg-[var(--theme-bg)] p-0 py-1.5 pl-0.5 pr-1.5 text-left text-xs transition-colors hover:bg-[var(--theme-accent)] box-border m-0 leading-tight"
                            >
                              <span
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid text-[9px] font-bold border-[var(--theme-border)] ${checked
                                  ? " bg-[#2563eb] text-white"
                                  : "bg-[var(--theme-bg)]"
                                  }`}
                              >
                                {checked ? "✓" : ""}
                              </span>
                              {league.logo && (
                                <img
                                  src={league.logo}
                                  alt=""
                                  className="h-5 w-5 shrink-0 object-contain"
                                />
                              )}
                              <span className="min-w-0 truncate text-left" title={league.nameVN || league.name}>
                                {displayName}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex shrink-0 items-center justify-between gap-2 border-t bg-[var(--theme-bg)] px-3 py-2">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="flex items-center gap-1.5 text-xs text-[var(--theme-text)] box-border m-0 p-0 border-none bg-transparent leading-none"
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid text-[10px] font-bold ${filteredLeagues.length > 0 && filteredLeagues.every((l) => tempSelectedIds.includes(l.id))
                      ? "border-[#2563eb] bg-[#2563eb] text-white"
                      : "border-[var(--theme-border)] bg-[var(--theme-bg)]"
                      }`}
                  >
                    {filteredLeagues.length > 0 && filteredLeagues.every((l) => tempSelectedIds.includes(l.id)) ? "✓" : ""}
                  </span>
                  Chọn tất cả
                </button>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="flex items-center gap-1.5 text-xs text-[var(--theme-text)] box-border m-0 p-0 border-none bg-transparent leading-none"
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid text-[10px] font-bold ${tempSelectedIds.length === 0
                      ? "border-[#2563eb] bg-[#2563eb] text-white"
                      : "border-[var(--theme-border)] bg-[var(--theme-bg)]"
                      }`}
                  >
                    {tempSelectedIds.length === 0 ? "✓" : ""}
                  </span>
                  Bỏ chọn tất cả
                </button>
              </div>
              <button
                type="button"
                onClick={handleConfirm}
                className="shrink-0 rounded-lg bg-[var(--theme-primary)] px-4 py-1.5 text-xs font-semibold text-[var(--theme-on-primary)] box-border m-0 border-none"
              >
                Xác nhận
              </button>
            </div>
          </div>,
          document.querySelector(".keobongvip-root") || document.body
        )}
    </div>
  );
}
